/**
 * FogBugz ↔ ADO project link (epic PI-SPEC-SW-E308, ER-1664 Package D;
 * additive within contract fogbugz 1).
 *
 * A FogBugz project is linked to one ADO project (the `container`) and,
 * optionally, a default ADO area path. Single FogBugz areas may override the
 * target with another ADO project and area path, for FogBugz projects that
 * span products. Project Managers set the link for their own ADO projects on
 * the Toolbox Project page; Admins anywhere. The FogBugz Formulator (which
 * repos to read), the case → ADO Bug tool (where the Bug lands) and the
 * product Q&A chat (which repos answer) resolve a case's FogBugz project and
 * area to a target with {@link fogbugzLinkResolutionSchema}.
 *
 * Also the ADO area-path list the link editor's picker reads
 * ({@link adoAreaPathListSchema}): area paths are picked, never typed (owner
 * decision 2026-10-29), and the server checks on save that each one exists
 * (`FOGBUGZ_LINK_AREA_PATH_NOT_FOUND`).
 *
 * The tracker is always `ado` (the only tracker SpecTree has).
 */
import { z } from "zod";
import { isoDateTime } from "../zod-helpers.js";
// ---------------------------------------------------------------------------
// Caps
// ---------------------------------------------------------------------------
/** Most per-area overrides one link carries. */
export const FOGBUGZ_LINK_AREA_OVERRIDES_MAX = 50;
/** Longest FogBugz project or area name, and longest ADO project name (`container`), a link stores. */
export const FOGBUGZ_LINK_NAME_MAX = 256;
/** Longest ADO area path a link stores. */
export const ADO_AREA_PATH_MAX = 512;
/**
 * Most area paths the area-path picker route returns for one ADO project
 * (owner decision 2026-10-29: no paging; past the cap the list is
 * `truncated` and the picker asks the person to type to filter).
 */
export const ADO_AREA_PATHS_MAX = 2000;
// ---------------------------------------------------------------------------
// Error codes
// ---------------------------------------------------------------------------
/** 409: the FogBugz project is already linked (one link per FogBugz project). */
export const FOGBUGZ_LINK_EXISTS = "FOGBUGZ_LINK_EXISTS";
/** 404: no link with that id. */
export const FOGBUGZ_LINK_NOT_FOUND = "FOGBUGZ_LINK_NOT_FOUND";
/** 409: the link changed after it was read for this edit or removal (its targets were checked on the old version). */
export const FOGBUGZ_LINK_CHANGED = "FOGBUGZ_LINK_CHANGED";
/** 400: an area path is not inside the ADO project it targets (see {@link isAreaPathInProject}). */
export const FOGBUGZ_LINK_AREA_PATH_INVALID = "FOGBUGZ_LINK_AREA_PATH_INVALID";
/** 400: an area path does not exist in the ADO project it targets (checked on save with the caller's ADO PAT). */
export const FOGBUGZ_LINK_AREA_PATH_NOT_FOUND = "FOGBUGZ_LINK_AREA_PATH_NOT_FOUND";
/** The plain sentence for {@link FOGBUGZ_LINK_AREA_PATH_NOT_FOUND}. */
export function FOGBUGZ_LINK_AREA_PATH_NOT_FOUND_MESSAGE(areaPath, project) {
    return `Area path ${areaPath} does not exist in ADO project ${project}.`;
}
/** Every FogBugz link API error code. */
export const FOGBUGZ_LINK_ERROR_CODES = [
    FOGBUGZ_LINK_EXISTS,
    FOGBUGZ_LINK_NOT_FOUND,
    FOGBUGZ_LINK_AREA_PATH_INVALID,
    FOGBUGZ_LINK_AREA_PATH_NOT_FOUND,
    FOGBUGZ_LINK_CHANGED,
];
// ---------------------------------------------------------------------------
// Area paths
// ---------------------------------------------------------------------------
/**
 * True when `areaPath` is the ADO project's root area (`Proj`) or below it
 * (`Proj\Area`), compared case-insensitively as ADO does. A cheap pre-check
 * only: whether the path exists is checked by the server.
 */
export function isAreaPathInProject(areaPath, project) {
    const path = areaPath.toLowerCase();
    const root = project.toLowerCase();
    return path === root || path.startsWith(`${root}\\`);
}
/** An ADO area path (`Proj\Area\Sub`), 1 to {@link ADO_AREA_PATH_MAX} characters. */
export const adoAreaPathStringSchema = z.string().min(1).max(ADO_AREA_PATH_MAX);
/** One area path in the picker list: the full path, its last segment, its depth (the project root is 0). */
export const adoAreaPathSchema = z.object({
    path: adoAreaPathStringSchema,
    name: z.string(),
    depth: z.number().int().min(0),
    hasChildren: z.boolean(),
});
/**
 * `GET /api/v1/grooming/projects/:project/ado-area-paths` → `data`: the
 * project's area paths, at most {@link ADO_AREA_PATHS_MAX}; `truncated` when
 * there were more.
 */
export const adoAreaPathListSchema = z.object({
    project: z.string(),
    paths: z.array(adoAreaPathSchema).max(ADO_AREA_PATHS_MAX),
    truncated: z.boolean(),
});
// ---------------------------------------------------------------------------
// Link input and view
// ---------------------------------------------------------------------------
/** A name a link stores: 1 to {@link FOGBUGZ_LINK_NAME_MAX} characters. */
const linkNameSchema = z.string().min(1).max(FOGBUGZ_LINK_NAME_MAX);
/** A FogBugz project (`ixProject`) or area (`ixArea`) id. */
const fogbugzIdSchema = z.number().int().positive();
/** An area path, or null for "no default area path". */
const nullableAreaPathSchema = adoAreaPathStringSchema.nullable();
/**
 * A mapped repository a link target reads: the same shape as the grooming
 * config's `GroomingRepoRef` (`grooming/types.ts`).
 */
export const fogbugzLinkRepoRefSchema = z.object({
    /** Tracker project that owns the repository, when it differs from the target's. */
    project: z.string().min(1).exactOptional(),
    repo: z.string().min(1),
    branch: z.string().min(1).exactOptional(),
});
/**
 * One FogBugz area sent to another ADO project (`container`) and, optionally,
 * area path. An absent or null `areaPath` means the target project's root.
 */
export const fogbugzAreaOverrideSchema = z.object({
    fogbugzAreaId: fogbugzIdSchema,
    fogbugzAreaName: linkNameSchema,
    container: linkNameSchema,
    areaPath: nullableAreaPathSchema.optional(),
});
/**
 * The body that creates or replaces a link. The default ADO project is the
 * one in the route (`:project`); `areaPath` is its default area path or null.
 * At most {@link FOGBUGZ_LINK_AREA_OVERRIDES_MAX} overrides, one per FogBugz
 * area.
 */
export const fogbugzProjectLinkInputSchema = z
    .object({
    fogbugzProjectId: fogbugzIdSchema,
    fogbugzProjectName: linkNameSchema,
    areaPath: nullableAreaPathSchema,
    overrides: z.array(fogbugzAreaOverrideSchema).max(FOGBUGZ_LINK_AREA_OVERRIDES_MAX),
})
    .superRefine((input, ctx) => {
    const seen = new Set();
    input.overrides.forEach((override, index) => {
        if (seen.has(override.fogbugzAreaId)) {
            ctx.addIssue({
                code: "custom",
                path: ["overrides", index, "fogbugzAreaId"],
                message: `FogBugz area ${String(override.fogbugzAreaId)} has more than one override`,
            });
        }
        seen.add(override.fogbugzAreaId);
    });
});
/** One ADO project a link targets (the default or an override) and the repos mapped there. */
export const fogbugzLinkTargetSchema = z.object({
    container: z.string(),
    repos: z.array(fogbugzLinkRepoRefSchema),
    /** True when grooming is set up on that ADO project (repos mapped), so code can be read. */
    codeSetUp: z.boolean(),
});
/**
 * A stored link as the Project page shows it. `editable` is true when the
 * caller may change it: `map-project` on every ADO project it targets (the
 * default and every override), or Admin.
 */
export const fogbugzProjectLinkViewSchema = z.object({
    id: z.string().min(1),
    fogbugzProjectId: fogbugzIdSchema,
    fogbugzProjectName: z.string(),
    tracker: z.literal("ado"),
    container: z.string(),
    areaPath: z.string().nullable(),
    overrides: z.array(fogbugzAreaOverrideSchema),
    editable: z.boolean(),
    targets: z.array(fogbugzLinkTargetSchema),
    updatedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
    updatedAt: isoDateTime({ offset: true }),
});
/**
 * An override in another ADO project's link that points into this one, so
 * the Project page can say which FogBugz areas land here.
 */
export const fogbugzLinkPointingHereSchema = z.object({
    linkId: z.string().min(1),
    fogbugzProjectName: z.string(),
    fogbugzAreaId: fogbugzIdSchema,
    fogbugzAreaName: z.string(),
    /** The ADO project that owns the link. */
    fromContainer: z.string(),
    areaPath: z.string().nullable(),
});
/** The links of one ADO project (the default target is that project) and the overrides pointing into it. */
export const fogbugzProjectLinksListSchema = z.object({
    links: z.array(fogbugzProjectLinkViewSchema),
    pointingHere: z.array(fogbugzLinkPointingHereSchema),
});
// ---------------------------------------------------------------------------
// Resolve
// ---------------------------------------------------------------------------
/**
 * `GET /api/v1/grooming/fogbugz-links/resolve` query. Numbers arrive as
 * strings and are coerced. With `fogbugzAreaId` an override for that area
 * wins over the project default.
 */
export const fogbugzLinkResolveQuerySchema = z.object({
    fogbugzProjectId: z.coerce.number().int().positive(),
    fogbugzAreaId: z.coerce.number().int().positive().optional(),
});
/** Where a resolved target came from: the area's override or the project default. */
export const FOGBUGZ_LINK_RESOLUTION_SOURCES = ["area-override", "project-default"];
/**
 * The resolve response: `not-linked`, or the ADO target with its mapped
 * repos. `callerHasRole` is true when the caller holds a Toolbox role on the
 * target ADO project (so they may read its code).
 */
export const fogbugzLinkResolutionSchema = z.discriminatedUnion("status", [
    z.object({
        status: z.literal("not-linked"),
        fogbugzProjectId: fogbugzIdSchema,
    }),
    z.object({
        status: z.literal("linked"),
        source: z.enum(FOGBUGZ_LINK_RESOLUTION_SOURCES),
        linkId: z.string().min(1),
        fogbugzProjectId: fogbugzIdSchema,
        tracker: z.literal("ado"),
        container: z.string(),
        areaPath: z.string().nullable(),
        repos: z.array(fogbugzLinkRepoRefSchema),
        codeSetUp: z.boolean(),
        callerHasRole: z.boolean(),
    }),
]);
//# sourceMappingURL=link.js.map